import { useEffect, useState } from 'react';
import { Brand } from './components/Brand';
import { accessToken, finishLogin, signOut, startLogin } from './auth';
import { api } from './api';
import { Landing } from './pages/Landing';
import { Legal } from './pages/Legal';
import { Onboarding } from './pages/Onboarding';
import { Workspace } from './pages/Workspace';
import type { OnboardingTurnRequest, OnboardingTurnResponse } from './lib/onboarding';
import type { OnboardingProfile, TenantSummary } from './types';

type AppPhase = 'onboarding' | 'workspace';

const DEMO_PROFILE_KEY = 'thisbot.demo.profile';
const TENANT_KEY = 'thisbot.active.tenant';
export const DEMO_WORKSPACE_PATH = '/app?demo=1&workspace=1';

function readProfile(key: string): OnboardingProfile | undefined {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as OnboardingProfile : undefined;
  } catch {
    return undefined;
  }
}

function tenantProfileKey(tenantId: string): string {
  return `thisbot.profile.${tenantId}`;
}

function writeProfile(key: string, profile: OnboardingProfile): void {
  try {
    localStorage.setItem(key, JSON.stringify(profile));
  } catch {
    // The server remains authoritative when browser storage is unavailable.
  }
}

export function resolveInitialAppState(search: string, savedDemoProfile?: OnboardingProfile): {
  demo: boolean;
  phase: AppPhase;
  profile?: OnboardingProfile;
} {
  const parameters = new URLSearchParams(search);
  const demo = parameters.get('demo') === '1';
  const opensSampleWorkspace = demo && parameters.get('workspace') === '1';
  return {
    demo,
    phase: opensSampleWorkspace ? 'workspace' : 'onboarding',
    profile: opensSampleWorkspace ? savedDemoProfile : undefined,
  };
}

export function shouldShowGlobalDemoBanner(demoContent: boolean, phase: AppPhase): boolean {
  return demoContent && phase === 'workspace';
}

export function tenantCreateBody(profile: OnboardingProfile): OnboardingProfile & { name: string } {
  return { ...profile, name: profile.brandName };
}

export async function completeOnboardingHandoff(
  profile: OnboardingProfile,
  persist: (profile: OnboardingProfile) => void | Promise<void>,
  enterWorkspace: (profile: OnboardingProfile) => void,
): Promise<void> {
  await persist(profile);
  enterWorkspace(profile);
}

export async function productionOnboardingTurn(request: OnboardingTurnRequest): Promise<OnboardingTurnResponse> {
  return api<OnboardingTurnResponse>('/onboarding/turn', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export function App() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const initial = resolveInitialAppState(window.location.search, readProfile(DEMO_PROFILE_KEY));
  const demo = initial.demo;
  const [profile, setProfile] = useState<OnboardingProfile | undefined>(initial.profile);
  const [tenantId, setTenantId] = useState<string | undefined>(() => demo ? undefined : sessionStorage.getItem(TENANT_KEY) ?? undefined);
  const [phase, setPhase] = useState<AppPhase>(initial.phase);
  const [loading, setLoading] = useState(path === '/app' && !demo && Boolean(accessToken()));
  const [loadError, setLoadError] = useState<string>();

  useEffect(() => {
    if (path !== '/app' || demo || !accessToken()) { setLoading(false); return; }
    let active = true;
    void api<{ tenants: TenantSummary[] }>('/tenants').then(({ tenants }) => {
      if (!active) return;
      const preferred = tenants.find((tenant) => tenant.tenantId === sessionStorage.getItem(TENANT_KEY)) ?? tenants[0];
      if (preferred) {
        sessionStorage.setItem(TENANT_KEY, preferred.tenantId);
        setTenantId(preferred.tenantId);
        setProfile(readProfile(tenantProfileKey(preferred.tenantId)));
        setPhase('workspace');
      } else {
        setTenantId(undefined);
        setPhase('onboarding');
      }
    }).catch((reason: unknown) => {
      if (active) setLoadError(reason instanceof Error ? reason.message : 'Your workspace could not be loaded.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [demo, path]);

  if (path === '/privacy') return <Legal kind="privacy" />;
  if (path === '/terms') return <Legal kind="terms" />;
  if (path === '/data-deletion') return <Legal kind="data-deletion" />;
  if (path === '/auth/callback') return <AuthCallback />;

  if (path === '/app') {
    if (loading) return <LoadingWorkspace />;
    if (loadError) return <div className="auth-callback"><Brand /><div className="auth-callback__card"><span className="auth-error">Workspace needs attention</span><h1>We could not load your account.</h1><p>{loadError}</p><button className="button button--blue" onClick={signOut}>Sign in again</button></div></div>;
    if (phase === 'workspace') {
      const workspaceDemo = demo || !tenantId;
      return <DemoFrame active={shouldShowGlobalDemoBanner(workspaceDemo, phase)}><Workspace tenantId={tenantId} demo={workspaceDemo} profile={profile} onReset={() => {
        if (demo) localStorage.removeItem(DEMO_PROFILE_KEY);
        else if (tenantId) localStorage.removeItem(tenantProfileKey(tenantId));
        setProfile(undefined);
        if (tenantId) signOut(); else setPhase('onboarding');
      }} /></DemoFrame>;
    }
    return <DemoFrame active={shouldShowGlobalDemoBanner(demo, phase)}><Onboarding onTurn={demo ? undefined : productionOnboardingTurn} onComplete={(next) => completeOnboardingHandoff(next, async (savedProfile) => {
      if (!demo) {
        if (!accessToken()) throw new Error('Sign in before creating a live workspace. No demo profile was saved.');
        const created = await api<{ tenantId: string }>('/tenants', {
          method: 'POST',
          body: JSON.stringify(tenantCreateBody(savedProfile)),
        });
        sessionStorage.setItem(TENANT_KEY, created.tenantId);
        writeProfile(tenantProfileKey(created.tenantId), savedProfile);
        setTenantId(created.tenantId);
      } else {
        writeProfile(DEMO_PROFILE_KEY, savedProfile);
      }
    }, (savedProfile) => {
      setProfile(savedProfile);
      setPhase('workspace');
    })} onOpenDemo={() => window.location.assign(DEMO_WORKSPACE_PATH)} /></DemoFrame>;
  }

  return <Landing onStart={() => void startLogin().catch(() => window.location.assign('/app?demo=1'))} />;
}

export function DemoFrame({ active, children }: { active: boolean; children: React.ReactNode }) {
  return <>{active && <div className="global-demo-banner" role="status"><strong>DEMO</strong><span>Sample data · no accounts are connected · nothing will publish</span></div>}{children}</>;
}

function LoadingWorkspace() {
  return <div className="auth-callback"><Brand /><div className="auth-callback__card"><span className="loading-mark" /><h1>Opening your isolated workspace…</h1><p>ThisBot is resolving your membership before any customer data is loaded.</p></div></div>;
}

function AuthCallback() {
  const [error, setError] = useState<string>();
  useEffect(() => { void finishLogin().catch((reason: unknown) => setError(reason instanceof Error ? reason.message : 'Sign-in failed.')); }, []);
  return <div className="auth-callback"><Brand /><div className="auth-callback__card">{error ? <><span className="auth-error">Sign-in needs attention</span><h1>We could not finish that authorization.</h1><p>{error}</p><a className="button button--blue" href="/">Try again</a></> : <><span className="loading-mark" /><h1>Finishing your secure sign-in…</h1><p>This window will move to your isolated workspace as soon as Cognito verifies the authorization code.</p></>}</div></div>;
}
